<template>
  <div class="complaint-manage">
    <!-- 查询条件 -->
    <vab-query-form>
      <vab-query-form-left-panel :span="20">
        <el-form inline @submit.prevent="query(1)">
          <el-form-item label="投诉人">
            <el-input v-model="queryParams.name" clearable placeholder="请输入投诉人" @change="query(1)" />
          </el-form-item>
          <el-form-item label="投诉类型">
            <el-select v-model="queryParams.complaintType" clearable placeholder="请选择" @change="query(1)">
              <el-option
                v-for="item in options[COMPLAINT_TYPE]"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="处理状态">
            <el-select v-model="queryParams.status" clearable placeholder="请选择" @change="query(1)">
              <el-option
                v-for="item in options[HANDLE_STATUS]"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="时间">
            <el-date-picker
              v-model="queryParams.time"
              end-placeholder="结束时间"
              range-separator="至"
              start-placeholder="开始时间"
              type="daterange"
              value-format="YYYY-MM-DD"
            />
          </el-form-item>
        </el-form>
      </vab-query-form-left-panel>
      <vab-query-form-right-panel :span="4">
        <el-button icon="search" type="primary" @click="query()">查询</el-button>
        <el-button icon="refresh" type="warning" @click="reset">重置</el-button>
      </vab-query-form-right-panel>
    </vab-query-form>

    <!-- 表格 -->
    <el-table v-loading="loading" border :data="tableData" :table-layout="'auto'">
      <el-table-column label="投诉人" prop="name" />
      <el-table-column label="投诉人ID" prop="uid" />
      <el-table-column label="投诉时间" prop="createTime" />
      <el-table-column label="投诉类型" prop="complaintType">
        <template #default="{ row }">
          {{ optionsMap[COMPLAINT_TYPE][row.complaintType] }}
        </template>
      </el-table-column>
      <el-table-column label="投诉原因" prop="complaintReason">
        <template #default="{ row }">
          {{ optionsMap[COMPLAINT_REASON][row.complaintReason] }}
        </template>
      </el-table-column>
      <el-table-column label="证明材料" width="200">
        <template #default="{ row }">
          <el-image
            v-for="(item, index) in row.evidence"
            :key="item"
            fit="contain"
            hight="200"
            :preview-src-list="row.evidence"
            :src="item"
            :style="index ? 'margin-left: 8px' : ''"
            width="200"
          />
        </template>
      </el-table-column>
      <el-table-column label="详细描述" prop="description">
        <template #default="{ row }">
          <TextOverflow :text="row.description" />
        </template>
      </el-table-column>
      <el-table-column label="联系方式" prop="contactNumber" />
      <el-table-column label="处置回复">
        <template #default="{ row }">
          {{ row.operateResp || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="处理状态">
        <template #default="{ row }">
          {{ optionsMap[HANDLE_STATUS][row.status] || '未处理' }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作">
        <template #default="{ row }">
          <el-link type="primary" :underline="false" @click="complaintFormModal.open(row)">处理</el-link>
        </template>
      </el-table-column>
      <template #empty>
        <el-empty class="vab-data-empty" description="暂无数据" />
      </template>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-model:current-page="queryParams.pageNum"
      background
      layout="prev, pager, next"
      :page-size="queryParams.pageSize"
      :total="queryParams.total"
      @current-change="query"
    />

    <!-- 处理 -->
    <el-dialog
      destroy-on-close
      :model-value="complaintFormModal.visible"
      title="处理"
      :width="480"
      @close="complaintFormModal.close()"
    >
      <ComplaintForm
        ref="complaintFormRef"
        v-bind="complaintFormModal.data"
        :options="options[HANDLE_STATUS]"
        @success="onComplaintSuccess"
      />
      <template #footer>
        <el-button @click="complaintFormModal.close()">取消</el-button>
        <el-button type="primary" @click="complaintFormRef.submit()">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted, watchEffect } from 'vue'
  import { omit } from 'lodash'
  import TextOverflow from '@/components/ui/TextOverflow.vue'
  import ComplaintForm from './ComplaintForm.vue'
  import { useModal } from '@/hooks/useModal'
  import { getComplaintJSON, getComplaintList } from '@/api/jbh'
  import dayjs from 'dayjs'

  const defaultQueryParams = {
    name: '',
    complaintType: '',
    status: '',
    time: [],
    startTime: '',
    endTime: '',
    pageNum: 1,
    pageSize: 20,
    total: 0,
  }
  const queryParams = reactive({ ...defaultQueryParams })

  watchEffect(() => {
    const [start, end] = queryParams.time || []
    if (start) queryParams.startTime = start
    if (end) queryParams.endTime = end
  })

  const HANDLE_STATUS = 'HANDLE_STATUS'
  const COMPLAINT_TYPE = 'COMPLAINT_TYPE'
  const COMPLAINT_REASON = 'COMPLAINT_REASON'
  const options = ref({})
  const optionsMap = ref({})

  const loading = ref(false)
  const tableData = ref([])

  onMounted(() => {
    query()
    getOptions()
  })

  function query(pageNum = queryParams.pageNum) {
    const params = {
      ...omit(queryParams, 'total', 'time'),
      type: 'COMPLAINTS',
      pageNum,
    }

    loading.value = true

    getComplaintList(params)
      .then(({ code, data }) => {
        if (code !== 200) return
        const { list, total } = data
        tableData.value = list.map((item) => {
          item.createTime = dayjs(item.createTime).format('YYYY-MM-DD HH:mm')
          try {
            item.evidence = JSON.parse(item.evidence).map((e) => e.src)
          } catch (e) {
            item.evidence = []
          }
          return item
        })
        queryParams.total = total
      })
      .finally(() => {
        loading.value = false
      })
  }

  function reset() {
    for (const k in defaultQueryParams) {
      queryParams[k] = defaultQueryParams[k]
    }
    query(1)
  }

  function getOptions() {
    getComplaintJSON().then((res) => {
      const map = {},
        opt = {}
      ;[HANDLE_STATUS, COMPLAINT_TYPE, COMPLAINT_REASON].forEach((key) => {
        const data = res.options[key]
        if (!Array.isArray(data)) return
        data.forEach(([value, label]) => {
          if (!map[key]) map[key] = {}
          if (!opt[key]) opt[key] = []
          map[key][value] = label
          if (key === HANDLE_STATUS && !value) return
          opt[key].push({ value, label })
        })
      })
      options.value = opt
      optionsMap.value = map
    })
  }

  function onComplaintSuccess() {
    query()
    complaintFormModal.close()
  }

  const complaintFormRef = ref()
  const complaintFormModal = useModal()
</script>

<style lang="scss" scoped>
  .complaint-manage {
    padding: 16px;
  }
</style>
